import { type ReactNode } from 'react';
import styled from 'styled-components';

import MuiSelect, { type SelectChangeEvent } from '@mui/material/Select';
import InputBase from '@mui/material/InputBase';
import MuiMenuItem from '@mui/material/MenuItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { SxProps, Theme } from '@mui/material/styles';

import theme from 'shared/themes/default';
import type { BackgroundThemes } from 'shared/const';

import {
  InputStyle,
  ButtonStyle,
  InputMenuStyle,
  InputItemStyle,
  ActionMenuStyle,
  ActionItemStyle,
} from './styles';

const ItemWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.m1};
  height: 100%;
`;

interface SelectItem {
  key: string | number;
  value: string | number | boolean;
  label: string;
  icon?: ReactNode;
}

interface CustomSelectProps {
  disabled?: boolean;
  fullWidth?: boolean;
  hasAction?: boolean;
  hasBackground?: boolean;
  iconOnly?: boolean;
  id: string;
  items?: SelectItem[];
  name: string;
  onChange: (target: {
    name: string;
    value: string | number | boolean | (string | number | boolean)[];
  }) => void;
  required?: boolean;
  sx?: SxProps<Theme>;
  theme?: BackgroundThemes;
  value?: string | number | boolean | (string | number | boolean)[];
}

const CustomSelect = ({
  fullWidth = true,
  hasAction,
  hasBackground,
  iconOnly,
  id,
  items = [],
  name,
  onChange,
  required,
  sx: sxProp = {},
  theme: themeLayout,
  value,
  ...props
}: CustomSelectProps) => {
  const handleChange = (
    event: SelectChangeEvent<
      string | number | boolean | (string | number | boolean)[]
    >
  ) => {
    onChange({
      name: event.target.name || name,
      value: event.target.value as
        string | number | boolean | (string | number | boolean)[],
    });
  };

  const textColor =
    themeLayout === 'dark' ? theme.palette.lightPure : theme.palette.mainDark;
  const disabledColor =
    themeLayout === 'dark'
      ? theme.palette.disabledDark
      : theme.palette.disabledLight;
  let colorIcon: string = hasBackground ? theme.palette.mainDark : textColor;
  colorIcon = hasAction ? colorIcon : theme.palette.darkDistinct;
  colorIcon = props.disabled ? disabledColor : colorIcon;

  const sx: SxProps<Theme> = {
    width: fullWidth ? '100%' : undefined,
    '& .MuiSelect-icon': {
      color: colorIcon,
    },
    ...sxProp,
  };

  const sxInputStyle = hasAction
    ? ButtonStyle(hasBackground, themeLayout)
    : InputStyle(themeLayout);
  const sxMenuList = hasAction ? ActionMenuStyle : InputMenuStyle;
  const sxMenuItem = hasAction ? ActionItemStyle : InputItemStyle;

  return (
    <MuiSelect
      id={id}
      name={name}
      value={value}
      onChange={handleChange}
      input={<InputBase />}
      SelectDisplayProps={
        {
          sx: sxInputStyle,
        } as React.HTMLAttributes<HTMLDivElement>
      }
      IconComponent={ExpandMoreIcon}
      sx={sx}
      MenuProps={{
        sx: sxMenuList,
      }}
      renderValue={
        iconOnly
          ? (selectedValue) => {
              const selectedItem = items.find(
                (item) => item.value === selectedValue
              );
              return <ItemWrapper>{selectedItem?.icon}</ItemWrapper>;
            }
          : undefined
      }
      required={required}
      {...props}
    >
      {items.map(({ key, value, label, icon }) => (
        <MuiMenuItem key={key} value={String(value)} sx={sxMenuItem}>
          {icon ? (
            <ItemWrapper>
              {icon}
              {label}
            </ItemWrapper>
          ) : (
            label
          )}
        </MuiMenuItem>
      ))}
    </MuiSelect>
  );
};

export default CustomSelect;
export type { CustomSelectProps, SelectItem };
